/**
 * TTS loudness: RMS-normalize every clip so consecutive messages sound the same level,
 * with a peak limiter to avoid clipping. Normalized WAV is played at elementVolume 1.
 */

import { isIOSDevice, isMobileBrowser } from './micPermission';

/** Target RMS loudness (perceived level) after normalization. */
const TARGET_RMS_DESKTOP = 0.07;
const TARGET_RMS_MOBILE = 0.06;
/** iOS play-and-record ducks speaker output — target higher than other mobile. */
const TARGET_RMS_IOS = 0.126;
/** Extra scale applied on top of RMS normalization. */
const TTS_MASTER_GAIN = 0.88;
const TTS_MASTER_GAIN_IOS = 1.0;
/** Never exceed this peak after gain (limiter). */
const MAX_PEAK_AFTER_GAIN = 0.98;
/** Cap gain boost for very quiet clips (avoids amplifying noise / silence-padded shorts). */
const MAX_GAIN = 4;
/** When trimming ElevenLabs lead/trail silence, sample is active above peak × this ratio. */
const SILENCE_TRIM_PEAK_RATIO = 0.02;
/**
 * iOS play-and-record: after SpeechRecognition releases the mic, system AGC boosts speaker
 * output for ~2–3s even though the waveform has no extra peaks. Ramp the next clip down.
 */
const IOS_POST_MIC_FADE_MS = 2800;
const IOS_POST_MIC_START_GAIN = 0.52;

let iosPostMicCaptureFadePending = false;

/** Call when iOS speech capture ends and voice output will play the next bot line. */
export function noteIosPostMicCaptureForPlayback() {
  iosPostMicCaptureFadePending = true;
}

function consumeIosPostMicCaptureFade() {
  if (!iosPostMicCaptureFadePending) return false;
  iosPostMicCaptureFadePending = false;
  return true;
}

function applyIosPostMicGainRamp(audioBuffer) {
  const sampleRate = audioBuffer.sampleRate;
  const fadeSamples = Math.min(
    audioBuffer.length,
    Math.ceil((IOS_POST_MIC_FADE_MS / 1000) * sampleRate),
  );
  if (fadeSamples <= 1) return;
  const startGain = IOS_POST_MIC_START_GAIN;
  for (let c = 0; c < audioBuffer.numberOfChannels; c += 1) {
    const data = audioBuffer.getChannelData(c);
    for (let i = 0; i < fadeSamples; i += 1) {
      const t = i / (fadeSamples - 1);
      data[i] *= startGain + (1 - startGain) * t;
    }
  }
}

function targetRms() {
  if (isIOSDevice()) return TARGET_RMS_IOS;
  return isMobileBrowser() ? TARGET_RMS_MOBILE : TARGET_RMS_DESKTOP;
}

function masterGain() {
  return isIOSDevice() ? TTS_MASTER_GAIN_IOS : TTS_MASTER_GAIN;
}

const preparedBlobBySource = new WeakMap();
/** Output blobs from renderNormalizedWavBlob — skip re-processing on play. */
const normalizedPlaybackBlobs = new WeakSet();
let decodeEngineWarmedUp = false;

function getOfflineAudioContextCtor() {
  if (typeof window === 'undefined') return null;
  return window.OfflineAudioContext || window.webkitOfflineAudioContext || null;
}

function measurePeak(audioBuffer) {
  let peak = 0;
  for (let c = 0; c < audioBuffer.numberOfChannels; c += 1) {
    const data = audioBuffer.getChannelData(c);
    for (let i = 0; i < data.length; i += 1) {
      const sample = Math.abs(data[i]);
      if (sample > peak) peak = sample;
    }
  }
  return peak;
}

function measureRms(audioBuffer) {
  let sumSquares = 0;
  let total = 0;
  for (let c = 0; c < audioBuffer.numberOfChannels; c += 1) {
    const data = audioBuffer.getChannelData(c);
    for (let i = 0; i < data.length; i += 1) {
      const sample = data[i];
      sumSquares += sample * sample;
      total += 1;
    }
  }
  if (total === 0) return 0;
  return Math.sqrt(sumSquares / total);
}

/** Ignore ElevenLabs lead/trail silence so short lines (e.g. naming thank-you) are not over-boosted. */
function findSpeechBounds(audioBuffer, peak) {
  const len = audioBuffer.length;
  if (len === 0) return { start: 0, end: 0 };
  const threshold = Math.max(peak * SILENCE_TRIM_PEAK_RATIO, 1e-5);
  const channels = audioBuffer.numberOfChannels;

  const isActive = (index) => {
    for (let c = 0; c < channels; c += 1) {
      if (Math.abs(audioBuffer.getChannelData(c)[index]) > threshold) return true;
    }
    return false;
  };

  let start = 0;
  while (start < len && !isActive(start)) start += 1;

  let end = len;
  while (end > start && !isActive(end - 1)) end -= 1;

  return { start, end };
}

function measureRmsInBounds(audioBuffer, start, end) {
  let sumSquares = 0;
  let total = 0;
  for (let c = 0; c < audioBuffer.numberOfChannels; c += 1) {
    const data = audioBuffer.getChannelData(c);
    for (let i = start; i < end; i += 1) {
      const sample = data[i];
      sumSquares += sample * sample;
      total += 1;
    }
  }
  if (total === 0) return 0;
  return Math.sqrt(sumSquares / total);
}

function measureLoudness(audioBuffer) {
  const peak = measurePeak(audioBuffer);
  const len = audioBuffer.length;
  if (len === 0 || peak <= 1e-6) {
    return { peak, rms: 0, speechRatio: 0 };
  }
  const { start, end } = findSpeechBounds(audioBuffer, peak);
  const speechLen = Math.max(end - start, 0);
  if (speechLen === 0) {
    return { peak, rms: measureRms(audioBuffer), speechRatio: 0 };
  }
  const rms = measureRmsInBounds(audioBuffer, start, end);
  return { peak, rms, speechRatio: speechLen / len };
}

/** RMS loudness match + peak limiter (peak-only norm makes short/long clips sound uneven). */
function computeGain(peak, rms, speechRatio = 1) {
  if (rms <= 1e-6) return 1;
  let gain = (targetRms() * masterGain()) / rms;
  // Sparse speech in a long clip (common on brief thank-yous) — limit extra boost.
  if (speechRatio > 0 && speechRatio < 0.4) {
    gain = Math.min(gain, MAX_GAIN * 0.75);
  }
  gain = Math.min(gain, MAX_GAIN);
  if (peak > 1e-5 && peak * gain > MAX_PEAK_AFTER_GAIN) {
    gain = MAX_PEAK_AFTER_GAIN / peak;
  }
  return gain;
}

function encodeWavBlob(audioBuffer) {
  const numChannels = audioBuffer.numberOfChannels;
  const sampleRate = audioBuffer.sampleRate;
  const length = audioBuffer.length;
  const bytesPerSample = 2;
  const blockAlign = numChannels * bytesPerSample;
  const dataSize = length * blockAlign;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);

  const writeString = (offset, str) => {
    for (let i = 0; i < str.length; i += 1) {
      view.setUint8(offset + i, str.charCodeAt(i));
    }
  };

  writeString(0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * blockAlign, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bytesPerSample * 8, true);
  writeString(36, 'data');
  view.setUint32(40, dataSize, true);

  let offset = 44;
  for (let i = 0; i < length; i += 1) {
    for (let c = 0; c < numChannels; c += 1) {
      const sample = Math.max(-1, Math.min(1, audioBuffer.getChannelData(c)[i]));
      view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
      offset += 2;
    }
  }

  return new Blob([buffer], { type: 'audio/wav' });
}

async function decodeBlobToBuffer(blob) {
  const Ctor = getOfflineAudioContextCtor();
  if (!Ctor) return null;
  try {
    const raw = await blob.arrayBuffer();
    const ctx = new Ctor(1, 1, 44100);
    return await ctx.decodeAudioData(raw.slice(0));
  } catch {
    return null;
  }
}

async function renderNormalizedWavBlob(blob) {
  const cached = preparedBlobBySource.get(blob);
  if (cached) return cached;

  try {
    const audioBuffer = await decodeBlobToBuffer(blob);
    if (!audioBuffer) return null;
    const { peak, rms, speechRatio } = measureLoudness(audioBuffer);
    const gain = computeGain(peak, rms, speechRatio);

    const Ctor = getOfflineAudioContextCtor();
    const offline = new Ctor(
      audioBuffer.numberOfChannels,
      audioBuffer.length,
      audioBuffer.sampleRate,
    );
    const source = offline.createBufferSource();
    const gainNode = offline.createGain();
    source.buffer = audioBuffer;
    gainNode.gain.value = gain;
    source.connect(gainNode);
    gainNode.connect(offline.destination);
    source.start(0);
    const rendered = await offline.startRendering();
    const wavBlob = encodeWavBlob(rendered);
    preparedBlobBySource.set(blob, wavBlob);
    normalizedPlaybackBlobs.add(wavBlob);
    return wavBlob;
  } catch {
    return null;
  }
}

async function applyIosPostMicFadeIfNeeded(blob) {
  if (!consumeIosPostMicCaptureFade()) {
    return blob;
  }
  const audioBuffer = await decodeBlobToBuffer(blob);
  if (!audioBuffer) return blob;
  applyIosPostMicGainRamp(audioBuffer);
  const faded = encodeWavBlob(audioBuffer);
  normalizedPlaybackBlobs.add(faded);
  return faded;
}

/** Prime OfflineAudioContext decode so the first real clip is not played un-normalized. */
export async function warmUpTtsLoudnessEngine() {
  if (decodeEngineWarmedUp || typeof window === 'undefined') return;
  const Ctor = getOfflineAudioContextCtor();
  if (!Ctor) return;
  decodeEngineWarmedUp = true;
  try {
    const ctx = new Ctor(1, 4410, 44100);
    const buf = ctx.createBuffer(1, 4410, 44100);
    await decodeBlobToBuffer(encodeWavBlob(buf));
  } catch {
    decodeEngineWarmedUp = false;
  }
}

/**
 * Decode, RMS-normalize, and re-encode each clip before HTMLAudioElement playback.
 * Gain is baked into the waveform — always use elementVolume 1.
 *
 * @param {Blob} blob
 * @returns {Promise<{ blob: Blob, elementVolume: number }>}
 */
export async function prepareTtsForPlayback(blob) {
  if (!blob) {
    return { blob, elementVolume: 1 };
  }

  let playbackBlob = blob;
  if (!normalizedPlaybackBlobs.has(blob)) {
    playbackBlob = (await renderNormalizedWavBlob(blob)) || blob;
    if (playbackBlob === blob) {
      await warmUpTtsLoudnessEngine();
      playbackBlob = (await renderNormalizedWavBlob(blob)) || blob;
    }
  }

  playbackBlob = await applyIosPostMicFadeIfNeeded(playbackBlob);

  return { blob: playbackBlob, elementVolume: 1 };
}

/** Fetch path + prefetch: store waveform-normalized audio so play() matches later clips. */
export async function normalizeTtsBlobForCache(blob) {
  if (!blob) return null;
  const { blob: playbackBlob } = await prepareTtsForPlayback(blob);
  return playbackBlob;
}

/** Skip silent autoplay prime — it triggers AGC (first clip loud, later clips drift). */
export function shouldSkipSilentAudioPrime() {
  return true;
}
